import * as React from 'react'
import { authManager } from '@/lib/auth'
import { isAxiosError } from '@/lib/api-client'
import type { Shop } from '@/types'

const LEGACY_SELECTED_STORE_KEY = 'selectedStore'
const USER_SELECTED_STORE_KEY_PREFIX = 'selectedStore:'
const SELECTED_STORE_EVENT = 'rp:selected-store-changed'

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isShop(value: unknown): value is Shop {
  return isRecord(value) && typeof value.id === 'string' && typeof value.shopName === 'string'
}

function buildSelectedStoreKey(userId: string): string {
  return `${USER_SELECTED_STORE_KEY_PREFIX}${encodeURIComponent(userId)}`
}

function readStoreFromKey(key: string): Shop | null {
  if (typeof window === 'undefined') return null

  const raw = localStorage.getItem(key)
  if (!raw) return null

  try {
    const parsed = JSON.parse(raw)
    return isShop(parsed) ? parsed : null
  } catch {
    return null
  }
}

function getCurrentSelectedStoreKey(): string | null {
  const userId = authManager.getUser()?.id
  return userId ? buildSelectedStoreKey(userId) : null
}

function getLegacySelectedStore(): Shop | null {
  return readStoreFromKey(LEGACY_SELECTED_STORE_KEY)
}

function emitSelectedStoreChange(): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new Event(SELECTED_STORE_EVENT))
}

function shouldNotifyForStorageKey(key: string | null): boolean {
  if (key === null) return true
  return key === LEGACY_SELECTED_STORE_KEY || key.startsWith(USER_SELECTED_STORE_KEY_PREFIX)
}

export function subscribeSelectedStore(listener: () => void): () => void {
  if (typeof window === 'undefined') {
    return () => {}
  }

  const handleStorage = (event: StorageEvent) => {
    if (!shouldNotifyForStorageKey(event.key)) return
    listener()
  }

  const handleSelectedStoreChange = () => {
    listener()
  }

  window.addEventListener('storage', handleStorage)
  window.addEventListener(SELECTED_STORE_EVENT, handleSelectedStoreChange)
  const unsubscribeAuth = authManager.subscribe(listener)

  return () => {
    window.removeEventListener('storage', handleStorage)
    window.removeEventListener(SELECTED_STORE_EVENT, handleSelectedStoreChange)
    unsubscribeAuth()
  }
}

export function getSelectedStore(): Shop | null {
  const key = getCurrentSelectedStoreKey()
  if (key) {
    const scopedStore = readStoreFromKey(key)
    if (scopedStore) {
      return scopedStore
    }

    return null
  }

  return getLegacySelectedStore()
}

export function setSelectedStore(store: Shop): void {
  if (typeof window === 'undefined') return

  const key = getCurrentSelectedStoreKey()
  if (!key) return

  localStorage.setItem(key, JSON.stringify(store))
  localStorage.removeItem(LEGACY_SELECTED_STORE_KEY)
  emitSelectedStoreChange()
}

export function clearSelectedStore(): void {
  if (typeof window === 'undefined') return

  const key = getCurrentSelectedStoreKey()
  if (!key) return

  localStorage.removeItem(key)
  emitSelectedStoreChange()
}

export function getSelectedShopId(): string {
  const selectedStore = getSelectedStore()
  const selectedShopId = selectedStore?.id
  return typeof selectedShopId === 'string' ? selectedShopId : ''
}

export function useSelectedShopId(): string {
  return React.useSyncExternalStore(subscribeSelectedStore, getSelectedShopId, () => '')
}

// `getSelectedStore()` parses localStorage and returns a NEW object every call.
// useSyncExternalStore requires a stable snapshot reference (else it loops with
// "getSnapshot should be cached"), so cache by serialized content: the same
// object reference is returned until the store's contents actually change.
let cachedStoreJson: string | null = null
let cachedStore: Shop | null = null
function getSelectedStoreSnapshot(): Shop | null {
  const store = getSelectedStore()
  const json = store ? JSON.stringify(store) : null
  if (json !== cachedStoreJson) {
    cachedStoreJson = json
    cachedStore = store
  }
  return cachedStore
}

/** The full selected store object (name, logo, …), reactively. */
export function useSelectedStore(): Shop | null {
  return React.useSyncExternalStore(subscribeSelectedStore, getSelectedStoreSnapshot, () => null)
}

/// Elysia validation failures arrive as a JSON blob serialized into `message`
/// ({"type":"validation","on":"body","property":"/points",...}). Shown raw it
/// is unreadable — extract the field name and the schema's human description
/// (or summary) instead. Returns null for anything that isn't such a blob.
function humanizeValidationMessage(raw: string): string | null {
  if (!raw.trimStart().startsWith('{')) return null
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!isRecord(parsed) || parsed.type !== 'validation') return null
    const field =
      typeof parsed.property === 'string' ? parsed.property.replace(/^\//, '') : ''
    let description: string | undefined
    if (Array.isArray(parsed.errors)) {
      for (const entry of parsed.errors) {
        if (isRecord(entry) && isRecord(entry.schema) && typeof entry.schema.description === 'string') {
          description = entry.schema.description
          break
        }
      }
    }
    const detail =
      description || (typeof parsed.summary === 'string' ? parsed.summary : '')
    if (field && detail) return `Invalid ${field}: ${detail}`
    if (detail) return detail
    return field ? `Invalid value for ${field}` : 'Validation failed'
  } catch {
    return null
  }
}

export function getErrorMessage(error: unknown, fallback = 'Something went wrong'): string {
  if (isAxiosError(error)) {
    // Prefer the server's message. Our API returns { message } JSON; some error
    // paths return a plain string body. Fall back to the generic axios message.
    const data: unknown = error.response?.data
    if (typeof data === 'string' && data.trim()) {
      return humanizeValidationMessage(data) ?? data
    }
    if (isRecord(data)) {
      const message = data.message
      if (typeof message === 'string' && message.trim()) {
        return humanizeValidationMessage(message) ?? message
      }
      if (Array.isArray(message) && message.length > 0) return String(message[0])
    }
    return error.message || fallback
  }
  if (error instanceof Error) return error.message
  return fallback
}
